import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, ShieldCheck, Clock, XCircle, LogOut, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

const Shell = ({ children }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-950 ptg-grid-bg p-4 flex items-center justify-center" data-testid="student-onboarding">
      <div className="w-full max-w-lg rounded-3xl bg-white p-8 sm:p-10 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-900 text-white"><GraduationCap className="h-6 w-6" /></div>
            <div><div className="font-heading text-lg font-extrabold text-slate-900">PTG Onboarding</div>
              <div className="text-[11px] font-semibold text-sky-700 uppercase tracking-widest">Complete your profile</div></div>
          </Link>
          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => { logout(); navigate("/student/login"); }} data-testid="onboarding-logout"><LogOut className="h-4 w-4 mr-1" /> Logout</Button>
        </div>
        {children}
      </div>
    </div>
  );
};

export default function StudentOnboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", date_of_birth: "", parent_name: "", parent_contact: "", parent_contact_alt: "", email: "", new_password: "", class_correction_note: "" });
  const set = (k) => (e) => setForm((x) => ({ ...x, [k]: e.target.value }));

  const load = async () => {
    try {
      const { data } = await api.get("/onboarding/me");
      setData(data);
      if (data.approved) { navigate("/student", { replace: true }); return; }
      const p = data.prefill || {};
      setForm((f) => ({ ...f, name: p.name || "", date_of_birth: p.date_of_birth || "", parent_name: p.parent_name || "", parent_contact: p.parent_contact || "", parent_contact_alt: p.parent_contact_alt || "", email: p.email || "" }));
    } catch (e) {
      toast.error("Could not load your onboarding details");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) { toast.error("Set a password of at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/onboarding/submit", form);
      toast.success("Submitted! Your account is pending PTG approval.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit");
    } finally { setBusy(false); }
  };

  if (loading) return <Shell><div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div></Shell>;

  const stage = data?.stage;

  if (stage === "pending_admin") {
    return (
      <Shell>
        <div className="text-center space-y-3 py-4" data-testid="onboarding-pending">
          <Clock className="h-12 w-12 text-amber-500 mx-auto" />
          <h1 className="text-2xl font-bold text-slate-900">Pending Admin Approval</h1>
          <p className="text-slate-600">Thank you! Your details for <b>Roll {data.roll_number}</b> have been submitted. PTG will confirm with your parent and approve your account. You'll be able to log in to the full portal once approved.</p>
          <p className="text-sm text-slate-500">You can close this page. No email verification is required.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <Seo title="Complete Onboarding" />
      {stage === "rejected" && (
        <div className="mb-5 rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex gap-2" data-testid="onboarding-rejected">
          <XCircle className="h-5 w-5 shrink-0" />
          <div>Your previous submission was not approved.{data.rejected_note ? <> Reason: <b>{data.rejected_note}</b></> : null} Please correct your details and submit again.</div>
        </div>
      )}
      <div className="mb-4 rounded-xl bg-sky-50 border border-sky-200 p-3 text-sm text-sky-900 flex gap-2">
        <ShieldCheck className="h-5 w-5 shrink-0" />
        <div>Complete your profile and set your own password. Your roll number and class come from PTG's records and cannot be changed here — request a correction if something looks wrong.</div>
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="onboarding-form">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="mb-1 block text-xs font-semibold text-slate-500">Roll Number (read-only)</Label>
            <Input value={data.roll_number || ""} readOnly className="bg-slate-100" data-testid="onboarding-roll" /></div>
          <div><Label className="mb-1 block text-xs font-semibold text-slate-500">Class / Std (read-only)</Label>
            <Input value={data.student_class || "—"} readOnly className="bg-slate-100" data-testid="onboarding-class" /></div>
        </div>
        <div><Label className="mb-1 block font-semibold text-slate-700">Student full name</Label>
          <Input value={form.name} onChange={set("name")} required data-testid="onboarding-name" /></div>
        <div><Label className="mb-1 block font-semibold text-slate-700">Date of birth</Label>
          <Input type="date" value={form.date_of_birth} onChange={set("date_of_birth")} required data-testid="onboarding-dob" /></div>
        <div><Label className="mb-1 block font-semibold text-slate-700">Parent / guardian name</Label>
          <Input value={form.parent_name} onChange={set("parent_name")} required data-testid="onboarding-parent-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="mb-1 block font-semibold text-slate-700">Parent contact (with country code)</Label>
            <Input value={form.parent_contact} onChange={set("parent_contact")} placeholder="+91XXXXXXXXXX" required data-testid="onboarding-contact" /></div>
          <div><Label className="mb-1 block font-semibold text-slate-700">Additional contact (optional)</Label>
            <Input value={form.parent_contact_alt} onChange={set("parent_contact_alt")} placeholder="+91XXXXXXXXXX" data-testid="onboarding-contact-alt" /></div>
        </div>
        <div><Label className="mb-1 block font-semibold text-slate-700">Email (optional)</Label>
          <Input type="email" value={form.email} onChange={set("email")} placeholder="optional" data-testid="onboarding-email" />
          <p className="text-xs text-slate-500 mt-1">No email is required. If you add one, it is used only as an optional backup.</p></div>
        <div><Label className="mb-1 block font-semibold text-slate-700">Set your password</Label>
          <Input type="password" value={form.new_password} onChange={set("new_password")} placeholder="At least 6 characters" required data-testid="onboarding-password" /></div>
        <div><Label className="mb-1 block font-semibold text-slate-700">Roll/Class correction request (optional)</Label>
          <Input value={form.class_correction_note} onChange={set("class_correction_note")} placeholder="Tell PTG if your roll/class looks wrong" data-testid="onboarding-correction" /></div>
        <Button type="submit" disabled={busy} className="w-full h-11 bg-blue-900 hover:bg-blue-950" data-testid="onboarding-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for approval"}
        </Button>
      </form>
    </Shell>
  );
}
